import { Component, Input, OnInit, AfterViewInit, ElementRef, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FilterField, FiltersConfig, FilterValues, FilterTypes } from '../../models/filter.interface';
import { DictionaryDTO } from 'src/app/core/models/dictionary';
import { countActiveFilters } from '../../helpers/filter.helper';
import { PaInputComponent } from '../../ui-kit/pa-input/pa-input.component';
import { PaSelectComponent } from '../../ui-kit/pa-select/pa-select.component';
import { PaMultiSelectComponent } from '../../ui-kit/pa-multi-select/pa-multi-select.component';
import { CommonModule } from '@angular/common';
import { ValueAccessorDirective } from '../../directives/value-accessor.directive';
import { ModalController } from '@ionic/angular/standalone';
import { PaDatepickerComponent } from '../../ui-kit/pa-datepicker/pa-datepicker.component';
import { PaInfoBlockComponent } from '../../ui-kit/pa-info-block/pa-info-block.component';
import { PaDatepickerRangePeriodComponent } from '../../ui-kit/pa-datepicker-range-period/pa-datepicker-range-period.component';
import { PaAutocompleteComponent } from '../../ui-kit/pa-autocomplete/pa-autocomplete.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PaInputComponent,
    PaSelectComponent,
    PaMultiSelectComponent,
    ValueAccessorDirective,
    PaDatepickerComponent,
    PaInfoBlockComponent,
    PaDatepickerRangePeriodComponent,
    PaAutocompleteComponent,
  ],
  selector: 'app-filters-sidebar',
  templateUrl: './filters-sidebar.component.html',
  styleUrls: ['./filters-sidebar.component.scss'],
})
export class FiltersSidebarComponent implements OnInit, AfterViewInit {
  @Input() config: FiltersConfig;

  /**
   * Ключ для формы группировки
   */
  readonly groupingFormKey = 'group_by';

  filterFields: FilterField[] = [];
  filterForm: FormGroup;

  private readonly elementRef = inject(ElementRef);

  constructor(
    private fb: FormBuilder,
    private modalCtrl: ModalController,
  ) {}

  ngOnInit(): void {
    this.filterFields = this.config?.filters || [];
    this.createForm();

    if (this.config.onComponentReady) {
      this.config.onComponentReady(this);
    }
  }

  ngAfterViewInit(): void {
    const key = this.config?.scrollToFilterKey;
    if (!key) return;

    const el = this.elementRef.nativeElement.querySelector(`[data-filter-key="${key}"]`) as HTMLElement | null;
    if (!el) return;

    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);

    setTimeout(() => {
      const calendarTrigger = el.querySelector<HTMLElement>('.p-datepicker-trigger, .p-calendar .p-inputtext, .p-datepicker .p-inputtext');

      if (calendarTrigger) {
        calendarTrigger.click();
        return;
      }

      const selectTrigger = el.querySelector<HTMLElement>('.p-select, .p-multiselect, .p-autocomplete, .p-datepicker, [role="combobox"]');
      if (selectTrigger) {
        selectTrigger.click();
        return;
      }

      const input = el.querySelector<HTMLInputElement>('input') ?? el.querySelector<HTMLTextAreaElement>('textarea');

      if (input) {
        input.click();
      }
    }, 500);
  }

  private createForm(): void {
    const formGroup: { [key: string]: AbstractControl } = {};

    if (this.config?.grouping) {
      const groupOptions = this.config.grouping.fields?.[0]?.options;
      const savedGroup = this.config.savedFilters?.[this.groupingFormKey];
      const defaultGroup = this.config.grouping.defaultGroup?.id;
      const rawGroup = savedGroup ?? defaultGroup ?? null;

      formGroup[this.groupingFormKey] = new FormControl(this.normalizeSelectValue(rawGroup, groupOptions));

      if (this.config.grouping.required) {
        formGroup[this.groupingFormKey].setValidators([Validators.required]);
      }
    }

    this.filterFields.forEach((field) => {
      const savedValue = this.config.savedFilters?.[field.dataKey];
      let rawValue = savedValue ?? null;
      if (field.disabled) {
        rawValue = null;
      }

      let value: unknown = rawValue;

      if (field.filterType === FilterTypes.Select) {
        value = this.normalizeSelectValue(rawValue, field.selectOptions);
      } else if (field.filterType === FilterTypes.MultiSelect) {
        value = this.normalizeMultiSelectValue(rawValue, field.selectOptions);
      } else if (field.filterType === FilterTypes.Month) {
        value = this.normalizeMonthValue(rawValue);
      }

      formGroup[field.dataKey] = new FormControl(value);
      if (field.required) {
        formGroup[field.dataKey].setValidators([Validators.required]);
      }
      if (field.disabled) {
        formGroup[field.dataKey].disable({ emitEvent: false });
      }
    });

    this.filterForm = this.fb.group(formGroup);

    this.filterForm.get(this.groupingFormKey)?.valueChanges.subscribe((id) => {
      const options = this.config?.grouping?.fields?.[0]?.options || [];
      const selected = options.find((o) => String(o.id) === String(id)) || null;
      this.config?.grouping?.onChange?.(selected);
    });
  }

  /**
   * Приводит значение периода к dd.mm.yyyy для month-пикера.
   * На вход могут прийти:
   * - yyyy-mm-dd (из URL/дефолта)
   * - dd.mm.yyyy (уже нормализовано pa-datepicker)
   */
  private normalizeMonthValue(raw: unknown): string | null {
    if (typeof raw !== 'string' || raw.trim().length === 0) {
      return null;
    }

    const value = raw.trim();

    if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
      return value;
    }

    if (/^\d{4}\-\d{2}\-\d{2}$/.test(value)) {
      const [year, month] = value.split('-');
      return `01.${month}.${year}`;
    }

    return null;
  }

  /**
   * Подставляет id из справочника, чтобы совпадать с типом option.id в p-select
   * (PrimeNG сравнивает строго; из URL всегда приходят строки).
   */
  private normalizeSelectValue(raw: unknown, options: DictionaryDTO[] | undefined): string | number | null {
    if (raw === null || raw === undefined || raw === '') {
      return raw as null;
    }
    if (!options?.length) {
      return raw as string | number;
    }
    const found = options.find((o) => String(o.id) === String(raw));
    return found ? found.id : (raw as string | number);
  }

  private normalizeMultiSelectValue(
    value: string | number | (string | number)[] | string[] | null | undefined,
    options: DictionaryDTO[] | undefined,
  ): (string | number)[] {
    if (value == null) {
      return [];
    }
    const arr = Array.isArray(value) ? value : [value];
    if (!options?.length) {
      return arr as (string | number)[];
    }
    return arr.map((v) => {
      const found = options.find((o) => String(o.id) === String(v));
      return found ? found.id : v;
    });
  }

  onApplyFilters(): void {
    if (this.filterForm.valid) {
      const filterValues = this.getNormalizedFilterValues(this.filterForm.getRawValue());
      const activeFiltersCount = this.getActiveFiltersCount(filterValues);

      if (this.config.onApply) {
        this.config.onApply(filterValues, activeFiltersCount);
      }
    }
  }

  private getNormalizedFilterValues(rawValues: FilterValues): FilterValues {
    const values: FilterValues = { ...rawValues };

    this.filterFields.forEach((field) => {
      if (field.filterType !== 'date-range') return;

      const v = values[field.dataKey] as unknown;

      if (Array.isArray(v)) {
        const [from, to] = v as (string | number | null | undefined)[];

        if (from && !to) {
          values[field.dataKey] = [from, from];
        } else if (from && to) {
          values[field.dataKey] = [from, to];
        }
      } else if (typeof v === 'string' && v) {
        const parts = v
          .split('-')
          .map((part) => part.trim())
          .filter(Boolean);

        if (parts.length >= 2) {
          values[field.dataKey] = [parts[0], parts[1]];
        } else if (parts.length === 1) {
          values[field.dataKey] = [parts[0], parts[0]];
        }
      }
    });

    return values;
  }

  onResetFilters(): void {
    this.filterForm.reset();
    this.filterFields.forEach((field) => {
      const control = this.filterForm.get(field.dataKey);
      if (field.disabled) {
        control?.setValue(null);
        return;
      }

      const defaultValue = this.config.defaultFilters?.[field.dataKey];

      if (defaultValue !== undefined && defaultValue !== null) {
        const nextDefault =
          field.filterType === FilterTypes.Month
            ? this.normalizeMonthValue(defaultValue)
            : defaultValue;
        control?.setValue(nextDefault);
      } else {
        control?.setValue(null);
      }
    });

    if (this.config.onReset) {
      this.config.onReset();
    }
  }

  onCloseModal(): void {
    this.modalCtrl.dismiss(null, 'close');
  }

  getFormValues(): FilterValues {
    return this.filterForm?.getRawValue() || {};
  }

  getActiveFiltersCount(filterValues?: FilterValues): number {
    const values = filterValues || this.filterForm?.getRawValue() || {};
    return countActiveFilters(values);
  }
}
